import { createRouter, createWebHistory } from 'vue-router'
import { ROLE_HOME, FULL_ACCESS_ROLES } from '@/config/navigation'
import { useAuthStore } from '@/store/useAuthStore'

// ── Auth ────────────────────────────────────────────────────────────────────
import Login from '@/components/Login.vue'

// ── Vistas públicas (sin login) ─────────────────────────────────────────────
import PublicInventarioView from '@/components/Inventario/PublicInventarioView.vue'
import PublicMoldesView from '@/components/Mantenimientos/PublicMoldesView.vue'
import PublicMaquinaView from '@/components/Maquinas/PublicMaquinasView.vue'
import PantallaProduccion from '@/components/TableroProduccion/TiempoReal/PantallaProduccion.vue'
import RedesSociales from '@/components/RedesSociales/redes.vue'
import PublicVenaScrap from '@/components/Inventario/VenaScrap/PublicVenaScrap.vue'
import PublicMateriaPrima from '@/components/Inventario/MateriaPrima/PublicMateriaPrima.vue'
import PublicScrapAlmacenLaser from '@/components/Inventario/ScrapAlmacenYLaser/PublicScrapAlmacenLaser.vue'
import PublicDisenosView from '@/components/TableroProduccion/Disenos/PublicDisenosView.vue'

// ── Paneles de administración (requieren auth) ──────────────────────────────
import AdminProduccion          from '@/components/Inventario/AdminProduccion.vue'
import AdminProcesos            from '@/components/Mantenimientos/AdminProcesos.vue'
import AdminPedidos             from '@/components/TableroProduccion/AdminPedidos.vue'
import AdminPedidosSeguimiento  from '@/components/TableroProduccion/AdminPedidosSeguimiento.vue'
import AdminPedidosLaser        from '@/components/TableroProduccion/AdminPedidosLaser.vue'
import AdminMaquinas            from '@/components/Maquinas/AdminMaquinas.vue'
import AdminInyeccion           from '@/components/Inyeccion/AdminInyeccion.vue'

// ── Panel superadmin / business_owner (acceso total) ────────────────────────
// Cuando lo tengas, crea este componente que muestre todo el sidebar.
// Por ahora apunta al mismo AdminProcesos o crea un AdminGeneral.vue.
import AdminGeneral from '@/components/Layout/AdminGeneral.vue'

// ── Mapa rol → ruta de inicio y roles de acceso total ───────────────────────
// Fuente única de verdad: src/config/navigation.js. Si agregas un rol, solo
// editas ahí.

// ───────────────────────────────────────────────────────────────────────────
const routes = [
  // Raíz
  { path: '/', redirect: '/login' },

  // ── Autenticación ──────────────────────────────────────────────────────
  {
    path: '/login',
    name: 'Login',
    component: Login,
  },

  {
    path: '/admin/general',
    name: 'AdminGeneral',
    component: AdminGeneral,
    meta: { requiresAuth: true }, // sin meta.role → el guard paso 3 lo permite
  },

  // ── Públicas (no requieren token) ──────────────────────────────────────
  {
    path: '/producto/:codigo',
    name: 'PublicProducto',
    component: PublicInventarioView,
  },
  {
    path: '/molde/:codigo',
    name: 'PublicMolde',
    component: PublicMoldesView,
  },
  {
    path: '/maquina/:id',
    name: 'PublicMaquina',
    component: PublicMaquinaView,
  },
  {
    path: '/produccion',
    name: 'PantallaProduccion',
    component: PantallaProduccion,
    meta: { public: true },
  },
  {
    path: '/redes-sociales',
    name: 'RedesSociales',
    component: RedesSociales,
    meta: { public: true },
  },
  {
    path: '/vena-scrap/',
    name: 'PublicVenaScrap',
    component: PublicVenaScrap,
    meta: { public: true },
  },
  {
    path: '/materia-prima',
    name: 'PublicMateriaPrima',
    component: PublicMateriaPrima,
    meta: { public: true },
  },
  {
    path: '/scrap-almacen-laser',
    name: 'PublicScrapAlmacenLaser',
    component: PublicScrapAlmacenLaser,
    meta: { public: true },
  },
  // Preaprobación de diseños: la abre el cliente desde el link de WhatsApp.
  // Sin acentos ni eñes en la ruta — un carácter no ASCII se percent-encodea
  // al viajar por WhatsApp y el link queda ilegible.
  {
    path: '/disenos/:token',
    name: 'PublicDisenos',
    component: PublicDisenosView,
    meta: { public: true },
  },

  // ── Paneles privados ───────────────────────────────────────────────────

  // Inventario → rol: produccion_materiales
  {
    path: '/admin/produccion',
    name: 'AdminProduccion',
    component: AdminProduccion,
    meta: { requiresAuth: true, role: 'produccion_materiales' },
  },

  // Mantenimientos / moldes → rol: procesos
  {
    path: '/admin/procesos',
    name: 'AdminProcesos',
    component: AdminProcesos,
    meta: { requiresAuth: true, role: 'procesos' },
  },

  // Clientes, artículos, pedidos → rol: gestion_pedidos
  {
    path: '/admin/pedidos',
    name: 'AdminPedidos',
    component: AdminPedidos,
    meta: { requiresAuth: true, role: 'gestion_pedidos' },
  },

  // Almacén / seguimiento de pedidos → rol: responsable_almacen
  {
    path: '/admin/pedidos-seguimiento',
    name: 'AdminPedidosSeguimiento',
    component: AdminPedidosSeguimiento,
    meta: { requiresAuth: true, role: 'responsable_almacen' },
  },

  // Láser → rol: responsable_laser
  {
    path: '/admin/pedidos-laser',
    name: 'AdminPedidosLaser',
    component: AdminPedidosLaser,
    meta: { requiresAuth: true, role: 'responsable_laser' },
  },

  // Máquinas / mantenimiento industrial → rol: mantenimientos
  {
    path: '/admin/maquinas',
    name: 'AdminMaquinas',
    component: AdminMaquinas,
    meta: { requiresAuth: true, role: 'mantenimientos' },
  },

  // Inyección (reporte de producción) → rol: responsable_inyeccion
  {
    path: '/admin/inyeccion',
    name: 'AdminInyeccion',
    component: AdminInyeccion,
    meta: { requiresAuth: true, role: 'responsable_inyeccion' },
  },

  // Catch-all → login
  { path: '/:pathMatch(.*)*', redirect: '/login' },
]

// ───────────────────────────────────────────────────────────────────────────
const router = createRouter({
  history: createWebHistory(),
  routes,
})

// ── Guard global ────────────────────────────────────────────────────────────
router.beforeEach((to, from, next) => {
  const authStore = useAuthStore()
  const token    = authStore.token
  const userRole = authStore.role

  // 0. Ya autenticado y cae en /login (o en "/", que redirige ahí) →
  //    mandarlo directo a su pantalla en vez de mostrarle el formulario.
  //    Antes esto no existía, así que un usuario con sesión válida
  //    igual terminaba viendo el login en cada visita a "/".
  if (to.path === '/login' && token) {
    const home = ROLE_HOME[userRole]
    if (home) return next(home)
  }

  // 1. Ruta pública explícita → siempre permitir
  if (to.meta.public) return next()

  // 2. Requiere auth y no hay token → login
  if (to.meta.requiresAuth && !token) return next('/login')

  // 3. Superadmin / business_owner → acceso total, sin restricción de rol
  if (to.meta.requiresAuth && FULL_ACCESS_ROLES.includes(userRole)) return next()

  // 4. Rol incorrecto para la ruta → redirigir al home del rol
  if (to.meta.role && to.meta.role !== userRole) {
    const home = ROLE_HOME[userRole]
    return home ? next(home) : next('/login')
  }

  // 5. Todo OK
  next()
})

export default router
